import ObjectPath from './objectPath';
import absoluteUrl from './absoluteUrl';
import guid from './guid';
import uuidv4 from './uuidv4';
import sortBy from './sortBy.js';
import writeScript from './writeScript.js';
import b64toBlob from './b64toBlob.js';
//import loadAndCacheDerivedDisplaySets from './loadAndCacheDerivedDisplaySets.js';
import urlUtil from './urlUtil';
import makeDeferred from './makeDeferred';
import makeCancelable from './makeCancelable';
import hotkeys from './hotkeys';
import Queue from './Queue';
import isDicomUid from './isDicomUid';
import formatDate from './formatDate';
import formatTime from './formatTime';
import formatPN from './formatPN';
import generateAcceptHeader from './generateAcceptHeader';
import resolveObjectPath from './resolveObjectPath';
import hierarchicalListUtils from './hierarchicalListUtils';
import progressTrackingUtils from './progressTrackingUtils';
import isLowPriorityModality from './isLowPriorityModality';
import { isImage } from './isImage';
import isDisplaySetReconstructable from './isDisplaySetReconstructable';
import sortInstancesByPosition from './sortInstancesByPosition';
import imageIdToURI from './imageIdToURI';
import debounce from './debounce';
import roundNumber from './roundNumber';
import downloadCSVReport from './downloadCSVReport';
import isEqualWithin from './isEqualWithin';
import addAccessors from './addAccessors';
import {
  sortStudy,
  sortStudySeries,
  sortStudyInstances,
  sortingCriteria,
  seriesSortCriteria,
  instancesSortCriteria,
} from './sortStudy';
import { splitComma, getSplitParam } from './splitComma';
import { createStudyBrowserTabs } from './createStudyBrowserTabs';
import { sopClassDictionary } from './sopClassDictionary';
import * as MeasurementFilters from './measurementFilters';
import getClosestOrientationFromIOP from './getClosestOrientationFromIOP';
import {
  cachedDicomFetch,
  clearDicomCache,
  dicomCacheKey,
  ensureDicomCacheSession,
  openDicomCache,
  DICOM_CACHE_NAME,
} from './dicomCache';
// Commented out unused functionality.
// Need to implement new mechanism for derived displaySets using the displaySetManager.

const BACKEND_URL_KEY = '__helical_backend_url__';

// Without this a crafted link could aim the viewer, and its uploads, at any host.
function isAllowedBackend(candidate: string): boolean {
  try {
    const url = new URL(candidate, window.location.origin);
    if (url.origin === window.location.origin) {
      return true;
    }
    const local = ['localhost', '127.0.0.1', '[::1]'];
    return local.includes(url.hostname) && local.includes(window.location.hostname);
  } catch {
    return false;
  }
}

function readStoredBackend(): string | null {
  try {
    return sessionStorage.getItem(BACKEND_URL_KEY);
  } catch {
    return null;
  }
}

function storeBackend(value: string): void {
  try {
    sessionStorage.setItem(BACKEND_URL_KEY, value);
  } catch {
    // storage disabled
  }
}

const stripTrailingSlash = (value: string) => value.replace(/\/+$/, '');

// Desktop serves the viewer and the API from one port, so the origin is the backend.
function getBackendUrl(): string {
  const fromQuery = new URLSearchParams(window.location.search).get('backendUrl');
  if (fromQuery && isAllowedBackend(fromQuery)) {
    storeBackend(fromQuery);
    return stripTrailingSlash(fromQuery);
  }

  const stored = readStoredBackend();
  if (stored && isAllowedBackend(stored)) {
    return stripTrailingSlash(stored);
  }

  return stripTrailingSlash(process.env.REACT_APP_BACKEND_URL || window.location.origin);
}

// cloud saves need an editor the API vouched for, the desktop always can
function canSaveRedaction(): boolean {
  try {
    if (!sessionStorage.getItem('helicalcloud_case')) {
      return true;
    }
    return sessionStorage.getItem('helicalcloud_can_edit') === '1';
  } catch {
    return false;
  }
}

const utils = {
  guid,
  uuidv4,
  ObjectPath,
  absoluteUrl,
  sortBy,
  sortBySeriesDate: sortStudySeries,
  sortStudy,
  sortStudySeries,
  sortStudyInstances,
  sortingCriteria,
  seriesSortCriteria,
  instancesSortCriteria,
  writeScript,
  formatDate,
  formatTime,
  formatPN,
  b64toBlob,
  urlUtil,
  imageIdToURI,
  //loadAndCacheDerivedDisplaySets,
  makeDeferred,
  makeCancelable,
  hotkeys,
  Queue,
  isDicomUid,
  isEqualWithin,
  sopClassDictionary,
  addAccessors,
  resolveObjectPath,
  hierarchicalListUtils,
  progressTrackingUtils,
  isLowPriorityModality,
  isImage,
  isDisplaySetReconstructable,
  debounce,
  roundNumber,
  downloadCSVReport,
  splitComma,
  getSplitParam,
  generateAcceptHeader,
  createStudyBrowserTabs,
  MeasurementFilters,
  getClosestOrientationFromIOP,
  getBackendUrl,
  canSaveRedaction,
  cachedDicomFetch,
  clearDicomCache,
  dicomCacheKey,
  ensureDicomCacheSession,
  openDicomCache,
  DICOM_CACHE_NAME,
};

export {
  guid,
  ObjectPath,
  absoluteUrl,
  sortBy,
  formatDate,
  writeScript,
  b64toBlob,
  urlUtil,
  //loadAndCacheDerivedDisplaySets,
  makeDeferred,
  makeCancelable,
  hotkeys,
  Queue,
  isDicomUid,
  isEqualWithin,
  resolveObjectPath,
  hierarchicalListUtils,
  progressTrackingUtils,
  isLowPriorityModality,
  isImage,
  isDisplaySetReconstructable,
  sortInstancesByPosition,
  imageIdToURI,
  debounce,
  roundNumber,
  downloadCSVReport,
  splitComma,
  getSplitParam,
  generateAcceptHeader,
  createStudyBrowserTabs,
  MeasurementFilters,
  getClosestOrientationFromIOP,
  getBackendUrl,
  canSaveRedaction,
  cachedDicomFetch,
  clearDicomCache,
  dicomCacheKey,
  ensureDicomCacheSession,
  openDicomCache,
  DICOM_CACHE_NAME,
};

export default utils;
